import { Cpu, FileText, Gauge, ShieldCheck } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwyfHxidXNpbmVzcyUyMG1lZXRpbmclMjBjb25zdWx0aW5nfGVufDB8fHx8MTc5MDg2MzY0NXww&ixlib=rb-4.1.0&q=85";

const PILLARS = [
  {
    icon: ShieldCheck,
    title: "Cybersecurity Engineering",
    desc: "Enterprise-grade architecture review, hardening, and defense strategy.",
  },
  {
    icon: Cpu,
    title: "Modern AI Technology",
    desc: "AI-augmented detection logic that cuts alert noise and surfaces real threats.",
  },
  {
    icon: FileText,
    title: "Security Operations Documentation",
    desc: "Operational documentation that turns process into repeatable practice.",
  },
  {
    icon: Gauge,
    title: "Operational Process Optimization",
    desc: "Streamlined SOC workflows, SLAs, and analyst experience.",
  },
];

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative py-20 sm:py-28 lg:py-32">
      <div className="container grid items-center gap-14 lg:grid-cols-2 lg:gap-20">
        {/* Treated image with clipped frame + spotlight */}
        <Reveal className="relative order-2 lg:order-1">
          <div className="absolute -inset-3 rounded-[28px] border border-cyan-400/15" />
          <div
            data-testid="about-image"
            className="group relative overflow-hidden rounded-3xl border border-white/10"
          >
            <img
              src={ABOUT_IMG}
              alt="GentecIT consultants in a strategy session"
              className="aspect-[4/3] w-full object-cover grayscale-[35%] transition-all duration-700 group-hover:scale-[1.02] group-hover:grayscale-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(16,185,129,0.14),transparent_55%)]" />
            <div className="absolute bottom-5 left-5 flex items-center gap-3 rounded-xl border border-white/10 bg-ink/85 px-4 py-3 backdrop-blur-xl">
              <span className="h-2 w-2 rounded-full bg-cyan-300 shadow-[0_0_12px_rgba(0,240,255,0.9)]" />
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-300">
                Senior architects &amp; threat engineers
              </p>
            </div>
          </div>
        </Reveal>

        {/* Copy + pillars */}
        <div className="order-1 lg:order-2">
          <SectionHeading
            index="01"
            label="Who we are"
            title="Engineering precision in an era of AI-driven threats"
          />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-400 sm:text-base">
              At GentecIT, we bridge the critical gap between executive
              security strategy and operational execution. Our team of senior
              cybersecurity architects and threat engineers craft bespoke
              operational workflows, eliminate alert fatigue, and fortify
              enterprise defenses with modern AI-augmented detection logic.
            </p>
          </Reveal>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.12 + i * 0.07}>
                <div
                  data-testid={`about-pillar-${i}`}
                  className="group h-full rounded-2xl border border-white/8 bg-white/[0.02] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-cyan-300/30 hover:bg-white/[0.04]"
                >
                  <p.icon className="h-5 w-5 text-cyan-300 transition-colors group-hover:text-emerald-300" />
                  <h3 className="mt-4 font-display text-sm font-bold text-slate-100">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">
                    {p.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
